import { useEffect, useRef, useState } from "react";
import CountUp from "react-countup";
import { FaUsers, FaUserTie, FaChartLine } from "react-icons/fa";

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
};

interface StatusItem {
  id: number;
  title: string;
  value: number;
  prefix: string | null;
  suffix: string | null;
  created_at: string;
  updated_at: string;
}

interface StatItem {
  id: number;
  icon: JSX.Element;
  value: number;
  prefix: string | null;
  suffix: string | null;
  title: string;
}

export default function CurrentStatusSection() {
  const [startCount, setStartCount] = useState(false);
  const [statusData, setStatusData] = useState<StatusItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchStatusData = async () => {
      try {
        const response = await fetch('https://ymfierp.yegnamicrofinance.com/api/current-statuses');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data: StatusItem[] = await response.json();
        setStatusData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
        console.error('Error fetching status data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStatusData();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStartCount(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.3,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const stats: StatItem[] = statusData.map((item): StatItem => {
    let icon: JSX.Element;
    switch (item.title.toLowerCase()) {
      case 'employees':
        icon = <FaUserTie className="text-2xl transition-colors duration-300" />;
        break;
      case 'customers':
        icon = <FaUsers className="text-2xl transition-colors duration-300" />;
        break;
      default:
        icon = <FaChartLine className="text-2xl transition-colors duration-300" />;
    }

    return {
      id: item.id,
      icon,
      value: item.value,
      prefix: item.prefix,
      suffix: item.suffix,
      title: item.title,
    };
  });

  if (loading) {
    return (
      <section className="py-12 px-4 sm:px-6" style={{ backgroundColor: colors.whiteOff }}>
        <div className="max-w-7xl mx-auto text-center">
          <p style={{ color: colors.textDark }}>Loading...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-12 px-4 sm:px-6" style={{ backgroundColor: colors.whiteOff }}>
        <div className="max-w-7xl mx-auto text-center">
          <p style={{ color: "#DC2626" }}>Error: {error}</p>
        </div>
      </section>
    );
  }

  return (
    <section 
      className="py-12 px-4 sm:px-6" 
      style={{ backgroundColor: colors.whiteOff }}
      ref={sectionRef}
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: colors.textDark }}>
            Current <span style={{ color: colors.primary }}>Status</span>
          </h2>
          <div 
            className="w-20 h-1 mx-auto rounded-full"
            style={{ backgroundColor: colors.primary }}
          />
        </div>

        {/* Stats Grid */}
        <div className="flex flex-wrap justify-center gap-6">
          {stats.map((stat) => {
            const isHovered = hoveredCard === stat.id;
            
            return (
              <div
                key={stat.id}
                className="w-[320px] sm:w-[400px] p-5 rounded-lg shadow-sm transition-all duration-300 border-l-4 hover:shadow-md cursor-pointer"
                style={{ 
                  backgroundColor: isHovered ? colors.primary : colors.white,
                  borderLeftColor: colors.primary
                }}
                onMouseEnter={() => setHoveredCard(stat.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div className="flex flex-col items-center text-center space-y-3">
                  <div 
                    className="p-3 rounded-full transition-colors duration-300"
                    style={{ 
                      backgroundColor: isHovered ? `${colors.white}20` : `${colors.primary}10`
                    }}
                  >
                    <div style={{ color: isHovered ? colors.white : colors.primary }}>
                      {stat.icon}
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold transition-colors duration-300">
                    <span style={{ color: isHovered ? colors.white : colors.textDark }}>
                      {stat.prefix}
                      <CountUp
                        start={startCount ? 0 : undefined}
                        end={stat.value}
                        duration={3}
                        separator=","
                      />
                      {stat.suffix}
                    </span>
                  </h3>
                  <p className="text-base font-medium transition-colors duration-300">
                    <span style={{ color: isHovered ? `${colors.white}E6` : colors.textDark }}>
                      {stat.title}
                    </span>
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}